import { useRequest } from 'ahooks'
import { Button, Form, Modal, Switch } from 'antd'
import { saveAs } from 'file-saver'
import { useAtom } from 'jotai'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { userInfoAtom } from '../user'

export const ProductionHomeExport = ({ selfIds }: { selfIds: string[] }) => {
  const { t } = useTranslation()
  const [viewer] = useAtom(userInfoAtom)
  const [visible, setVisible] = React.useState(false)
  const [form] = Form.useForm()

  const { data: productionExportRoles } = useRequest('/api/production/export/roles')

  const { run: exportProduction, loading: exportLoading } = useRequest(
    (data) => ({
      url: '/api/production/export/excel',
      method: 'POST',
      data,
      responseType: 'blob',
    }),
    {
      manual: true,
      onSuccess: (res) => {
        const date = moment().utcOffset(8).format('YYYYMMDD_HHmmss')
        saveAs(res, `production_${date}.xlsx`)
      },
    }
  )

  const roles = productionExportRoles || []
  const canExport = viewer.isSuperAdmin || viewer.isMiniIPDCore || roles.includes(viewer.email)

  if (!canExport) {
    return null
  }

  return (
    <>
      <Button type='link' loading={exportLoading} onClick={() => setVisible(true)}>
        {t('export production')}
      </Button>
      <Modal
        title={t('export production')}
        visible={visible}
        onOk={() => {
          const fields = form.getFieldsValue()
          exportProduction({ selfIds, fields })
          setVisible(false)
        }}
        onCancel={() => {
          setVisible(false)
        }}
        okButtonProps={{ loading: exportLoading }}
        cancelButtonProps={{ disabled: exportLoading }}
      >
        <Form
          form={form}
          initialValues={{
            selfId: true,
            name: true,
            email: true,
            startDate: true,
            endDate: true,
            classification: true,
            stage: true,
            trackingResult: true,
            expectDeliveryTime: true,
            confirmedDeliveryTime: true,
            actualDeliveryTime: true,
            projectType: true,
            requirement: true,
            situationDescription: true,
            competition: true,
            businessResult: true,
            userAcceptance: true,
            productQuality: true,
            marketIndicators: true,
            expectProductionTime: true,
            expectTargetCheckTime: true,
            flowStatus: true,
            predictWorkload: true,
            actualWorkload: true,
            background: true,
            standard: true,
            priority: true,
          }}
        >
          <Form.Item name='selfId' label={t('index')} valuePropName='checked'>
            <Switch disabled />
          </Form.Item>
          <Form.Item name='email' label={t('production.detail.email')} valuePropName='checked'>
            <Switch disabled />
          </Form.Item>
          <Form.Item name='name' label={t('production.detail.name')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='startDate' label={t('production.detail.startDate')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='endDate' label={t('production.detail.endDate')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='classification' label={t('production.detail.classification')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='stage' label={t('production.detail.stage')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='trackingResult' label={t('production.detail.trackingResult')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item
            name='expectDeliveryTime'
            label={t('production.detail.expectDeliveryTime')}
            valuePropName='checked'
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name='confirmedDeliveryTime'
            label={t('production.flowStatus.DevOnGoing.TOConfirm.form.confirmDeliveryTime.title')}
            valuePropName='checked'
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name='actualDeliveryTime'
            label={t('production.flowStatus.DevOnGoing.TOConfirm.form.actualDeliveryTime.title')}
            valuePropName='checked'
          >
            <Switch />
          </Form.Item>
          <Form.Item name='projectType' label={t('production.detail.projectType')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='requirement' label={t('production.detail.requirement')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item
            name='situationDescription'
            label={t('production.detail.situationDescription')}
            valuePropName='checked'
          >
            <Switch />
          </Form.Item>
          <Form.Item name='competition' label={t('production.detail.competition')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='businessResult' label={t('production.detail.businessResult')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='userAcceptance' label={t('production.detail.userAcceptance')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='productQuality' label={t('production.detail.productQuality')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='marketIndicators' label={t('production.detail.marketIndicators')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item
            name='expectProductionTime'
            label={t('production.detail.expectProductionTime')}
            valuePropName='checked'
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name='expectTargetCheckTime'
            label={t('production.detail.expectTargetCheckTime')}
            valuePropName='checked'
          >
            <Switch />
          </Form.Item>
          <Form.Item name='flowStatus' label={t('production.status.title')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='predictWorkload' label={t('production.detail.predictWorkload')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='actualWorkload' label={t('production.actualWorkload.title')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='background' label={t('production.detail.background')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='standard' label={t('production.detail.standard')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='priority' label={t('production.detail.priority')} valuePropName='checked'>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
